import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, FileText, Loader2, Download, Lock } from "lucide-react";

const CATEGORIES = [
  { v: "convention", l: "Convention" },
  { v: "contrat_travail", l: "Contrat de travail" },
  { v: "bulletin_paie", l: "Bulletin de paie" },
  { v: "dpae", l: "DPAE" },
  { v: "justificatif", l: "Justificatif" },
  { v: "medical", l: "Médical" },
  { v: "ged_generale", l: "GED générale" },
];
const SALARIE_LINKED = new Set(["contrat_travail", "bulletin_paie", "dpae"]);
const CONFID = [
  { v: "public", l: "Public" },
  { v: "interne", l: "Interne" },
  { v: "confidentiel", l: "Confidentiel" },
  { v: "medical_restreint", l: "Médical restreint (réservé)" },
];

export default function GED() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titre: "", categorie: "ged_generale", entreprise_id: "", salarie_id: "", confidentialite: "interne", link_to_salarie: false });
  const [file, setFile] = useState(null);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    const [a, b, c] = await Promise.all([api.get("/documents"), api.get("/entreprises"), api.get("/salaries").catch(() => ({ data: [] }))]);
    setItems(a.data); setEntreprises(b.data); setSalaries(c.data || []); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choisissez un fichier"); return; }
    const needsSalarie = SALARIE_LINKED.has(form.categorie);
    if (needsSalarie && form.link_to_salarie && !form.salarie_id) {
      toast.error("Sélectionnez le salarié à lier"); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("titre", form.titre);
      fd.append("categorie", form.categorie);
      if (form.entreprise_id) fd.append("entreprise_id", form.entreprise_id);
      if (form.link_to_salarie && form.salarie_id) fd.append("salarie_id", form.salarie_id);
      fd.append("confidentialite", form.confidentialite);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(form.link_to_salarie ? "Document versé et lié au salarié" : "Document versé à la GED");
      setOpen(false); setFile(null);
      setForm({ titre: "", categorie: "ged_generale", entreprise_id: "", salarie_id: "", confidentialite: "interne", link_to_salarie: false });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const download = async (id, filename) => {
    try {
      const resp = await api.get(`/documents/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? items : items.filter(d => d.categorie === filter);

  return (
    <div data-testid="ged-page">
      <PageHeader
        title="GED — Gestion électronique des documents ESS"
        subtitle="Classement, confidentialité, versioning et audit. Médical restreint réservé."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="ged-upload-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <Upload className="h-4 w-4 mr-2" /> Verser un document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Verser un document à la GED</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2"><Label>Titre *</Label><Input required value={form.titre} onChange={(e)=>setForm({...form, titre: e.target.value})} data-testid="ged-titre" /></div>
                <div className="space-y-2">
                  <Label>Catégorie *</Label>
                  <Select value={form.categorie} onValueChange={(v)=>setForm({...form, categorie: v})}>
                    <SelectTrigger data-testid="ged-categorie"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c=><SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Entreprise</Label>
                  <Select value={form.entreprise_id} onValueChange={(v)=>setForm({...form, entreprise_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                    <SelectContent>{entreprises.map(e=><SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {SALARIE_LINKED.has(form.categorie) && (
                  <div className="space-y-2 rounded-lg bg-blue-50 border border-blue-200 p-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={form.link_to_salarie} onCheckedChange={(v) => setForm({ ...form, link_to_salarie: !!v })} data-testid="ged-link-salarie" />
                      <strong>Lier au salarié</strong>
                      <span className="text-xs text-slate-500">(alimente automatiquement le registre des salariés)</span>
                    </label>
                    {form.link_to_salarie && (
                      <Select value={form.salarie_id} onValueChange={(v)=>setForm({...form, salarie_id: v})}>
                        <SelectTrigger data-testid="ged-salarie-select"><SelectValue placeholder="Choisir un salarié…" /></SelectTrigger>
                        <SelectContent>
                          {salaries
                            .filter(s => !form.entreprise_id || s.entreprise_id === form.entreprise_id)
                            .map(s => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} {s.poste && `· ${s.poste}`}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Confidentialité *</Label>
                  <Select value={form.confidentialite} onValueChange={(v)=>setForm({...form, confidentialite: v})}>
                    <SelectTrigger data-testid="ged-confidentialite"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONFID.map(c => (
                        <SelectItem key={c.v} value={c.v} disabled={c.v === "medical_restreint" && user?.role !== "admin" && user?.role !== "medical"}>{c.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Fichier *</Label><Input ref={fileRef} type="file" required onChange={(e)=>setFile(e.target.files?.[0])} data-testid="ged-file" /></div>
                <DialogFooter><Button type="submit" disabled={uploading} data-testid="ged-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verser"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mb-5 flex gap-2 flex-wrap">
        <button onClick={()=>setFilter("all")} className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${filter === "all" ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white text-slate-700 border-slate-200 hover:border-[#C5A059]"}`} data-testid="ged-filter-all">Tous ({items.length})</button>
        {CATEGORIES.map(c => {
          const cnt = items.filter(d => d.categorie === c.v).length;
          return (
            <button key={c.v} onClick={()=>setFilter(c.v)} className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${filter === c.v ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white text-slate-700 border-slate-200 hover:border-[#C5A059]"}`} data-testid={`ged-filter-${c.v}`}>{c.l} ({cnt})</button>
          );
        })}
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div> :
       filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <FileText className="h-10 w-10 mx-auto text-slate-300" /><p className="mt-3 text-slate-500">Aucun document.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ged-list">
          {filtered.map(d => (
            <div key={d.id} className="kpi-card bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between">
                <FileText className="h-5 w-5 text-[#C5A059]" />
                <div className="flex items-center gap-1.5">
                  {d.confidentialite === "medical_restreint" && <Lock className="h-3 w-3 text-red-500" />}
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">v{d.version}</span>
                </div>
              </div>
              <h3 className="mt-3 font-semibold text-slate-900 line-clamp-2" style={{ fontFamily: "Outfit, sans-serif" }}>{d.titre}</h3>
              <div className="mt-2 flex gap-2 flex-wrap">
                <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700">{d.categorie}</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-amber-50 text-amber-700">{d.confidentialite}</span>
              </div>
              <div className="mt-3 text-xs text-slate-500 truncate">{d.original_filename}</div>
              <div className="text-[11px] text-slate-400 mt-1">{Math.round((d.size || 0) / 1024)} Ko · {d.created_at?.slice(0,10)}</div>
              <Button size="sm" variant="outline" className="mt-4 w-full" onClick={()=>download(d.id, d.original_filename)} data-testid={`ged-dl-${d.id}`}>
                <Download className="h-3.5 w-3.5 mr-2" /> Télécharger
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
